import { useState, useContext, createContext, useEffect } from "react";
import axios from "axios";
import { useAuth } from "./auth";

const WishlistContext = createContext();

const WishlistProvider = ({ children }) => {
  const [auth] = useAuth();
  const [wishlist, setWishlist] = useState([]);
  const [loading, setLoading] = useState(false);

  // Get wishlist from backend when user is logged in
  const getWishlist = async () => {
    if (!auth?.user || !auth?.token) {
      // Load from localStorage if not logged in
      const localWishlist = localStorage.getItem("wishlist");
      if (localWishlist) {
        setWishlist(JSON.parse(localWishlist));
      }
      return;
    }

    try {
      setLoading(true);
      const { data } = await axios.get("/api/v1/auth/wishlist");
      if (data?.success) {
        setWishlist(data.wishlist || []);
      }
    } catch (error) {
      console.log(error);
      // Fallback to localStorage on error
      const localWishlist = localStorage.getItem("wishlist");
      if (localWishlist) {
        setWishlist(JSON.parse(localWishlist));
      }
    } finally {
      setLoading(false);
    }
  };

  // Check if product is in wishlist
  const isInWishlist = (productId) => {
    return wishlist.some((item) => item._id === productId);
  };

  // Add to wishlist
  const addToWishlist = async (product) => {
    // Check if already in wishlist
    if (isInWishlist(product._id)) {
      return { success: false, message: "Product already in wishlist" };
    }

    if (!auth?.user || !auth?.token) {
      // Use localStorage if not logged in
      const localWishlist = JSON.parse(localStorage.getItem("wishlist") || "[]");
      const exists = localWishlist.find((item) => item._id === product._id);
      if (!exists) {
        localWishlist.push(product);
        localStorage.setItem("wishlist", JSON.stringify(localWishlist));
        setWishlist(localWishlist);
        return { success: true, message: "Added to wishlist" };
      }
      return { success: false, message: "Already in wishlist" };
    }

    try {
      const { data } = await axios.post("/api/v1/auth/wishlist", {
        productId: product._id,
      });
      if (data?.success) {
        setWishlist([...wishlist, product]);
        return { success: true, message: data.message };
      }
      return { success: false, message: data.message };
    } catch (error) {
      console.log(error);
      return { success: false, message: "Failed to add to wishlist" };
    }
  };

  // Remove from wishlist
  const removeFromWishlist = async (productId) => {
    if (!auth?.user || !auth?.token) {
      // Use localStorage if not logged in
      const localWishlist = JSON.parse(localStorage.getItem("wishlist") || "[]");
      const updated = localWishlist.filter((item) => item._id !== productId);
      localStorage.setItem("wishlist", JSON.stringify(updated));
      setWishlist(updated);
      return { success: true, message: "Removed from wishlist" };
    }

    try {
      const { data } = await axios.delete(`/api/v1/auth/wishlist/${productId}`);
      if (data?.success) {
        setWishlist(wishlist.filter((item) => item._id !== productId));
        return { success: true, message: data.message };
      }
      return { success: false, message: data.message };
    } catch (error) {
      console.log(error);
      return { success: false, message: "Failed to remove from wishlist" };
    }
  };

  // Load wishlist on mount and when auth changes
  useEffect(() => {
    getWishlist();
  }, [auth?.user, auth?.token]);

  return (
    <WishlistContext.Provider
      value={{
        wishlist,
        setWishlist,
        addToWishlist,
        removeFromWishlist,
        isInWishlist,
        loading,
        getWishlist,
      }}
    >
      {children}
    </WishlistContext.Provider>
  );
};

// Custom hook
const useWishlist = () => useContext(WishlistContext);

export { useWishlist, WishlistProvider };

